import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { isWorkspaceReadOnly, shouldRetry } from "@brevalt/api-client";
import { getGetCurrentTenantQueryKey, getGetMeQueryKey } from "@brevalt/api-client/customer";

export interface QueryClientOptions {
  /** Called for failed mutations (and queries marked `meta: { toastOnError: true }`), e.g. an error toast. */
  onError?: (error: unknown) => void;
}

/**
 * The query client of the customer app and the console: no retry of 4xx (shouldRetry), and a 403
 * `workspace-read-only` refreshes `/me` and the tenant so the read-only banner and disabled actions appear (HANDOFF §0.16).
 */
export function createQueryClient({ onError }: QueryClientOptions = {}): QueryClient {
  let client: QueryClient | null = null;
  const handle = (error: unknown) => {
    if (!isWorkspaceReadOnly(error)) return;
    // The workspace state lives in /me (entitlements) and /tenants/current (the deletion date).
    void client?.invalidateQueries({ queryKey: getGetMeQueryKey() });
    void client?.invalidateQueries({ queryKey: getGetCurrentTenantQueryKey() });
  };
  client = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        handle(error);
        if (query.meta?.toastOnError === true) onError?.(error);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _vars, _ctx, mutation) => {
        handle(error);
        if (mutation.meta?.toastOnError !== false) onError?.(error);
      },
    }),
    defaultOptions: {
      queries: { retry: shouldRetry, staleTime: 30_000, refetchOnWindowFocus: true },
      mutations: { retry: false },
    },
  });
  return client;
}
